import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ConfigProvider, Space, Table } from 'antd';
import { useAuthZ } from '../../authZ/useAuthZ';
import EditUserModal from './EditUserModal';
import AddUserToProfileModal from './AddUserToProfileModal';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';

const UsersTable = ({ users, refreshUserList, ...rest }) => {
	const authZ = useAuthZ();

	const [isLoading, setIsLoading] = useState(true);
	const [usersList, setUsersList] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [editUserModalIsVisible, setEditUserModalIsVisible] = useState(false);
	const [addUserToProfileModalIsVisible, setAddUserToProfileModalIsVisible] = useState(false);
	const { t, getAntLocale } = useI18nLocalization();

	useEffect(() => {
		if (users) {
			setUsersList(getUsersList());
			setIsLoading(false);
		}
	}, [JSON.stringify(users), isLoading]);

	const getUsersList = () => {
		const usersList = _.map(users, (u) => {
			const userListItem = {
				key: u.profile.userId,
				active: u.profile.userEnabled,
				firstName: u.profile.firstName,
				lastName: u.profile.lastName,
				employeeId: u.profile.employeeId,
				emailAddress: u.profile.emailAddress,
				jobTitle: u.profile.jobTitle,
				roles: u.roles
			};
			return userListItem;
		});

		return usersList;
	};

	const handleShowEditUserModal = (userSelected) => {
		const su = users.find((u) => {
			return u.profile.userId === userSelected.key;
		});
		setSelectedUser(su);
		setEditUserModalIsVisible(true);
	};

	const handleShowAddUserToProfileModal = (userSelected) => {
		const su = users.find((u) => {
			return u.profile.userId === userSelected.key;
		});
		setSelectedUser(su);
		setAddUserToProfileModalIsVisible(true);
	};

	return (
		<>
			<ConfigProvider locale={getAntLocale()}>
				<Table
					dataSource={usersList}
					pagination={{
						position: ['bottomRight'],
						defaultPageSize: 100,
						showSizeChanger: true,
						pageSizeOptions: [1, 100, 250, 500, 1000],
						showTotal: (total) => (total === 1 ? `${total} ${t('user')}` : `${total} ${t('users')}`)
					}}
					loading={isLoading}
					{...rest}
				>
					<Table.Column
						title={t('First Name')}
						dataIndex='firstName'
						key='firstName'
						sorter={{
							compare: (a, b) => {
								return a.firstName < b.firstName ? -1 : a.firstName > b.firstName ? 1 : 0;
							}
						}}
					/>
					<Table.Column
						title={t('Last Name')}
						dataIndex='lastName'
						key='lastName'
						sorter={{
							compare: (a, b) => {
								return a.lastName < b.lastName ? -1 : a.lastName > b.lastName ? 1 : 0;
							}
						}}
					/>
					<Table.Column
						title={t('Employee Id')}
						dataIndex='employeeId'
						key='employeeId'
						sorter={{
							compare: (a, b) => {
								return a.employeeId < b.employeeId ? -1 : a.employeeId > b.employeeId ? 1 : 0;
							}
						}}
						responsive={['lg']}
					/>
					<Table.Column
						title={t('Email Address')}
						dataIndex='emailAddress'
						key='emailAddress'
						sorter={{
							compare: (a, b) => {
								return a.emailAddress < b.emailAddress ? -1 : a.emailAddress > b.emailAddress ? 1 : 0;
							}
						}}
						responsive={['lg']}
					/>
					<Table.Column
						title={t('Job Title')}
						dataIndex='jobTitle'
						key='jobTitle'
						sorter={{
							compare: (a, b) => {
								return a.jobTitle < b.jobTitle ? -1 : a.jobTitle > b.jobTitle ? 1 : 0;
							}
						}}
						responsive={['xl']}
					/>
					<Table.Column
						title={t('Roles')}
						dataIndex='roles'
						key='roles'
						align='center'
						render={(text, user) => {
							return (
								<Space direction='vertical'>
									{_.map(_.orderBy(user.roles, ['role'], ['asc']), (r) => {
										return <span key={`${r.userId}-${r.roleId}`}>{r.role}</span>;
									})}
								</Space>
							);
						}}
						responsive={['md']}
					/>
					<Table.Column
						title={t('Active')}
						dataIndex='active'
						key='active'
						sorter={{
							compare: (a, b) => {
								return a.active < b.active ? -1 : a.active > b.active ? 1 : 0;
							}
						}}
						align='center'
						render={(text, user) => {
							return user.active ? (
								<i className='las la-check la-lg primary-color'></i>
							) : (
								<i className='las la-times la-lg'></i>
							);
						}}
						responsive={['md']}
					/>
					{authZ.userIsEntDevAdmin() && (
						<>
							<Table.Column
								title={t('Edit')}
								dataIndex='edit'
								key='edit'
								align='center'
								render={(text, user) => {
									return (
										<i
											className='las la-user-edit la-lg primary-color cursor-pointer'
											onClick={() => {
												handleShowEditUserModal(user);
											}}
										></i>
									);
								}}
							/>
							<Table.Column
								title={t('Add to Profile')}
								dataIndex='addToProfile'
								key='addToProfile'
								align='center'
								render={(text, user) => {
									return (
										<i
											className='las la-user-plus la-lg primary-color cursor-pointer'
											onClick={() => {
												handleShowAddUserToProfileModal(user);
											}}
										></i>
									);
								}}
							/>
						</>
					)}
				</Table>
			</ConfigProvider>
			{authZ.userIsEntDevAdmin() && (
				<>
					<EditUserModal
						isVisible={editUserModalIsVisible}
						onSaveCallback={() => {
							refreshUserList();
							setEditUserModalIsVisible(false);
						}}
						onCloseCallback={() => {
							setEditUserModalIsVisible(false);
						}}
						user={selectedUser}
					/>
					<AddUserToProfileModal
						isVisible={addUserToProfileModalIsVisible}
						onSaveCallback={() => {
							setAddUserToProfileModalIsVisible(false);
						}}
						onCloseCallback={() => {
							setAddUserToProfileModalIsVisible(false);
						}}
						user={selectedUser}
					/>
				</>
			)}
		</>
	);
};

UsersTable.propTypes = {
	users: PropTypes.array,
	refreshUserList: PropTypes.func.isRequired
};

export default UsersTable;
